import { useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type Handsontable from 'handsontable/base';

// Utils
import { colorBorders } from '../utils/domUtils/tableCellBorderUtils';
import type { MilanoteHotTableInstance, MilanoteHotTableProps } from '../tableHotInstanceTypes';

type MilanoteHotTableEditingProps = Pick<
    MilanoteHotTableProps,
    'locale' | 'filterQuery' | 'showTitle' | 'showCaption' | 'isResizing' | 'gridSize' | 'isDarkMode'
>;

/**
 * Custom hook for managing table cell editing side effects.
 * Handles syncing props to Handsontable instance.
 */
export const useTableCellEditingEffects = ({
    locale,
    filterQuery,
    isReadOnly,
    showTitle,
    showCaption,
    isResizing,
    gridSize,
    isDarkMode,
    isSingleSelected,
    hotTableInstanceRef,
}: {
    locale: string;
    filterQuery?: string;
    isReadOnly: boolean;
    showTitle: boolean;
    showCaption: boolean;
    isResizing: boolean;
    gridSize: number;
    isDarkMode: boolean;
    isSingleSelected: boolean;
    hotTableInstanceRef: MutableRefObject<Handsontable | null>;
}): void => {
    /**
     * Update locale in Handsontable instance and re-render.
     */
    useEffect(() => {
        const hotInstance =
            hotTableInstanceRef.current as MilanoteHotTableInstance<MilanoteHotTableEditingProps> | null;
        if (!hotInstance || hotInstance.isDestroyed) return;

        hotInstance.milanoteProps.locale = locale;
        hotInstance.render();
    }, [hotTableInstanceRef, locale]);

    /**
     * Update filterQuery value in the hotTableInstance.
     */
    useEffect(() => {
        const hotInstance =
            hotTableInstanceRef.current as MilanoteHotTableInstance<MilanoteHotTableEditingProps> | null;
        if (!hotInstance) return;

        hotInstance.milanoteProps.filterQuery = filterQuery;
        hotInstance.render();
    }, [filterQuery, hotTableInstanceRef]);

    /**
     * Update settings for readonly table.
     */
    useEffect(() => {
        if (!isReadOnly) return;

        // disable all shortcuts
        hotTableInstanceRef.current?.unlisten();
    }, [hotTableInstanceRef, isReadOnly]);

    /**
     * Update showTitle value in the hotTableInstance.
     */
    useEffect(() => {
        const hotInstance =
            hotTableInstanceRef.current as MilanoteHotTableInstance<MilanoteHotTableEditingProps> | null;
        if (!hotInstance) return;

        hotInstance.milanoteProps.showTitle = showTitle;
    }, [hotTableInstanceRef, showTitle]);

    /**
     * Update showCaption value in the hotTableInstance.
     */
    useEffect(() => {
        const hotInstance =
            hotTableInstanceRef.current as MilanoteHotTableInstance<MilanoteHotTableEditingProps> | null;
        if (!hotInstance) return;

        hotInstance.milanoteProps.showCaption = showCaption;
    }, [hotTableInstanceRef, showCaption]);

    /**
     * Update isResizing value in the hotTableInstance.
     */
    useEffect(() => {
        const hotInstance =
            hotTableInstanceRef.current as MilanoteHotTableInstance<MilanoteHotTableEditingProps> | null;
        if (!hotInstance) return;

        hotInstance.milanoteProps.isResizing = isResizing;
    }, [hotTableInstanceRef, isResizing]);

    /**
     * Update gridSize and destroy editor when grid size changes.
     */
    useEffect(() => {
        const hotInstance =
            hotTableInstanceRef.current as MilanoteHotTableInstance<MilanoteHotTableEditingProps> | null;
        if (!hotInstance) return;

        hotInstance.milanoteProps.gridSize = gridSize;

        // Destroy the editor so that it can be recalculated in new grid size
        requestAnimationFrame(() => {
            if (!hotTableInstanceRef.current || hotTableInstanceRef.current.isDestroyed) return;

            hotTableInstanceRef.current.destroyEditor();
        });
    }, [gridSize, hotTableInstanceRef]);

    /**
     * Update relevant values with new darkmode setting.
     */
    useEffect(() => {
        const hotInstance = hotTableInstanceRef.current as MilanoteHotTableInstance | null;
        if (!hotInstance) return;

        hotInstance.milanoteProps.isDarkMode = isDarkMode;

        // recalculate the coloured cell borders with the new darkmode setting
        const dataArray = hotInstance.getData();
        for (let row = 0; row < dataArray.length; row++) {
            for (let col = 0; col < dataArray[row].length; col++) {
                colorBorders(hotInstance, row, col);
            }
        }
    }, [hotTableInstanceRef, isDarkMode]);
};

export default useTableCellEditingEffects;
